import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { AddPlusIcon } from '../../../vectors/index';
import CloseIcon from '@mui/icons-material/Close';
import * as yup from 'yup';
import { getStrippedText } from '@utils/helper';
import isEmail from 'validator/lib/isEmail';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Position } from '@staffedupusa/shared-components';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneIcon from '@mui/icons-material/Done';
import { useGetJobPostings } from '@features/jobPostings/hooks/useGetJobPostings';
// import { useUserCountry } from '@context/UserCountryContext';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { useCreateApplication } from '@features/applications/hooks/useCreateApplication';
import { toast } from 'react-toastify';
import { bold } from '@styles/fontWeights';
import { darkBlue } from '@styles/colors';
import { HelpOutline, Upload } from '@mui/icons-material';
import UploadTeamDialog from './UploadTeamDialog';
interface AddTeamMemberModalProps {
  isOpen: boolean;
  localJobPostings: Position[];
  handleClose: () => void;
}

type FormData = {
  job_listing_ids: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
};

function AddTeamMemberModal({
  isOpen,
  handleClose,
  localJobPostings,
}: AddTeamMemberModalProps) {
  const [errorText, setErrorText] = useState<string>('');
  const [uploadTeamMemberListModalOpen, setUploadTeamMemberListModalOpen] =
    useState(false);
  // const { userCountry } = useUserCountry();

  const { jobPostingsData } = useGetJobPostings();
  const { createApplication, isCreateApplicationLoading } =
    useCreateApplication({
      setErrorText,
    });
  const schema: yup.ObjectSchema<FormData> = yup
    .object({
      job_listing_ids: yup.string().required('Position is required'),
      first_name: yup
        .string()
        .required('First Name is required')
        .test('not-empty-html', 'First Name cannot be empty', (value) => {
          if (!value) return false;
          return getStrippedText(value).length > 0;
        }),
      last_name: yup
        .string()
        .required('Last Name is required')
        .test('not-empty-html', 'Last Name cannot be empty', (value) => {
          if (!value) return false;
          return getStrippedText(value).length > 0;
        }),
      email: yup
        .string()
        .required('Email is required')
        .test('is-email', 'Must be a valid email', (value: string) =>
          isEmail(value),
        ),
      phone_number: yup
        .string()
        .required('Phone is required')
        .test(
          'is-phone-number',
          'Must be a valid phone number',
          function (value: string) {
            const phone = value?.trim() || '';

            // if (userCountry === 'au') {
            //   // Allow 9 or 10 digits only (no symbols, no spaces)
            //   return /^\d{9,10}$/.test(phone);
            // }

            // Default to US validation
            return isMobilePhone(phone, ['en-US']);
          },
        ),
    })
    .required();

  const {
    handleSubmit,
    control,
    formState: { errors, touchedFields },
  } = useForm<FormData>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues: {
      job_listing_ids: '',
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      // hired: false,
    },
  });
  const onSubmit = (values: FormData) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data && data?.status === 200) {
          toast.success('Team member added successfully');
          setErrorText('');
          handleClose();
          // reset();
        } else {
          setErrorText(data.message);
        }
      },
    };
    createApplication(
      {
        ...values,
        hired: true,
      },
      options,
    );
  };
  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => {
          //   reset();
          handleClose();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <br />
          <AddPlusIcon />
          <br />
          <Typography variant="h4" mt={1}>
            Add a Team Member
          </Typography>
        </DialogTitle>
        <DialogContent>
          {errorText && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorText}
            </Alert>
          )}
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="addApplicantForm"
          >
            <Stack
              spacing={3}
              sx={{
                pt: 1,
                '& p': {
                  ml: 1,
                  fontSize: '15px',
                },
              }}
            >
              <Controller
                control={control}
                name="first_name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.first_name)}
                    helperText={
                      errors?.first_name ? errors?.first_name.message : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <>
                          {errors.first_name && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.first_name && !errors.first_name && (
                            <CheckCircleIcon
                              color="success"
                              fontSize="medium"
                            />
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="last_name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.last_name)}
                    helperText={
                      errors?.last_name ? errors?.last_name.message : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <>
                          {errors.last_name && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.last_name && !errors.last_name && (
                            <CheckCircleIcon
                              color="success"
                              fontSize="medium"
                            />
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors?.email ? errors?.email.message : ''}
                    InputProps={{
                      endAdornment: (
                        <>
                          {errors.email && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.email && !errors.email && (
                            <CheckCircleIcon
                              color="success"
                              fontSize="medium"
                            />
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="phone_number"
                render={({ field }) => (
                  <TextField
                    {...field}
                    hiddenLabel
                    label="Phone"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.phone_number)}
                    helperText={errors?.phone_number?.message ?? ''}
                    InputProps={{
                      inputProps: {
                        style: {
                          fontSize: '15px',
                        },
                      },
                      endAdornment: (
                        <>
                          {errors.phone_number && (
                            <CancelIcon color="error" fontSize="medium" />
                          )}
                          {touchedFields.phone_number &&
                            !errors.phone_number && (
                              <CheckCircleIcon
                                color="success"
                                fontSize="medium"
                              />
                            )}
                        </>
                      ),
                    }}
                    InputLabelProps={{
                      style: {
                        fontSize: '15px',
                      },
                    }}
                    FormHelperTextProps={{
                      style: {
                        fontSize: '13px',
                      },
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="job_listing_ids"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.job_listing_ids)}>
                    <Select
                      {...field}
                      value={field.value.split(',')}
                      size="small"
                      multiple={false}
                      displayEmpty
                      onChange={(e) =>
                        field.onChange(e.target.value as string[])
                      }
                    >
                      <MenuItem disabled value="">
                        <em>{`Select Position`}</em>
                      </MenuItem>
                      {jobPostingsData?.object &&
                        jobPostingsData.object
                          .filter(
                            (item: any) =>
                              item.status !== 1 && item.status !== 4,
                          )
                          .map((jobPosting: Position) => (
                            <MenuItem
                              key={jobPosting.id}
                              value={String(jobPosting.id)}
                            >
                              {jobPosting.s_title}
                            </MenuItem>
                          ))}
                    </Select>
                    {errors?.job_listing_ids && (
                      <FormHelperText>
                        {errors?.job_listing_ids.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              {jobPostingsData?.object &&
                jobPostingsData?.object?.length === 0 && (
                  <Typography variant="body2" color="error">
                    No active jobs available. To add a position not listed here,
                    move the hiring status from closed to accepting or hiring
                    under Job Position management for this location
                  </Typography>
                )}

              <LoadingButton
                type="submit"
                startIcon={<DoneIcon />}
                variant="contained"
                size="small"
                fullWidth
                loading={isCreateApplicationLoading}
                loadingPosition="start"
              >
                Add Team Member
              </LoadingButton>
              <Stack
                direction="row"
                sx={{ width: '100%' }}
                spacing={2}
                alignItems="center"
              >
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  sx={{ width: '100%', height: 'fit-content' }}
                  onClick={() => setUploadTeamMemberListModalOpen(true)}
                >
                  Upload Team Member List
                </Button>
                <Tooltip
                  title={
                    <span>
                      Upload a CSV file to bulk add team members. Ensure the
                      format matches the sample file. -{' '}
                      <a
                        href={`${import.meta.env.VITE_S3_PATH}assets/csv/Team-members.csv`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          cursor: 'pointer',
                          fontWeight: bold,
                          color: darkBlue,
                          textDecoration: 'underline',
                        }}
                      >
                        Download Sample
                      </a>
                    </span>
                  }
                  placement="top"
                  arrow
                >
                  <HelpOutline color="secondary" fontSize="small" />
                </Tooltip>
              </Stack>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
      {uploadTeamMemberListModalOpen && (
        <UploadTeamDialog
          open={uploadTeamMemberListModalOpen}
          onClose={() => {
            setUploadTeamMemberListModalOpen(false);
            handleClose();
          }}
          positions={localJobPostings}
        />
      )}
    </>
  );
}

export default AddTeamMemberModal;
